import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { useAppSelector } from '../../app/hooks';
import { useUpdatePatientCoCMMutation } from '../../app/services/patient';
import theme from '../../app/theme';
import { useToast } from '../../app/toast';
import { selectUser } from '../../features/auth/authSlice';
import { selectProviders } from '../../features/patient/patientSlice';
import { ContentPopup } from '../../types/CommonType';
import { CoCMStatusType } from '../../types/PatientType';
import { Provider } from '../../types/UserTypes';
import { CoCMCallActionType } from '../utils/callActionType';
import { Role } from '../utils/loginRole';
import { CoCMStatus, getNumberInUSFormat, getCurrentMonthTimeTracker } from '../utils/patientUtils';
import { canViewTimeTracker, isCareManagerOrSuperAdmin } from '../utils/permissionUtils';
import PopupModal from './PopupModal';
import MoreTimeIcon from '@mui/icons-material/MoreTime';
import IconButton from '@mui/material/IconButton';

const StyleAutocomplete = styled(Autocomplete)(() => ({
  width: '100%',
}));

const StyleLeftBox = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  flexBasis: '40%',
  [theme.breakpoints.down('sm')]: {
    flexBasis: '30%',
  },
}));

const StyleRightBox = styled(Box)(() => ({
  flexBasis: '60%',
  [theme.breakpoints.down('sm')]: {
    flexBasis: '70%',
  },
}));

const StyleLayout = styled(Box)(() => ({
  columnGap: 1,
  display: 'flex',
  justifyContent: 'flex-start',
  marginBottom: 2,
  width: '100%',
  [theme.breakpoints.up('md')]: {
    justifyContent: 'space-between',
  },
}));

const StyleTypo = styled(Typography)(() => ({
  fontSize: '15px!important',
}));

const StyleTextField = styled(TextField)(() => ({
  width: '100%',
  [`& .MuiFormLabel-root`]: {
    fontSize: '15px',
  },
  [`& .MuiOutlinedInput-root`]: {
    fontSize: '15px',
  },
}));

interface CoCMSettingFormProps {
  coCMStatusId?: number;
  careManagerId?: number;
  episodesOfCare?: number;
  totalTimeTrackerMinutes?: number;
  onReload?: () => void;
}

const defaultPopupContent: ContentPopup = {
  title: '',
  description: '',
  btnOk: '',
  btnClose: '',
  toastMessage: '',
};

export default function CoCMSettingForm({
  coCMStatusId,
  careManagerId,
  episodesOfCare,
  totalTimeTrackerMinutes,
  onReload,
}: CoCMSettingFormProps) {
  const { t } = useTranslation();
  const { patientId } = useParams();
  const currentUser = useAppSelector(selectUser);
  const providers = useAppSelector(selectProviders);
  const toast = useToast();
  const navigate = useNavigate();

  const [coCMStatus, setCoCMStatus] = useState<CoCMStatusType | undefined>(
    CoCMStatus.find((item: CoCMStatusType) => item.id === coCMStatusId)
  );
  const [careManager, setCareManager] = useState<Provider | null>(
    providers.find((user: Provider) => user.id === careManagerId) || null
  );
  const [episodes, setEpisodes] = useState<number | undefined>(episodesOfCare);
  const [coCMStatusIdAction, setCoCMStatusIdAction] = useState<number | undefined>(undefined);
  const [careManagerIdAction, setCareManagerIdAction] = useState<number | undefined>(undefined);
  const [callAction, setCallAction] = useState<string>('');
  const [isOpen, setOpen] = useState<boolean>(false);
  const [contentPopup, setContentPopup] = useState<ContentPopup>(defaultPopupContent);

  const [updateCoCMSetting] = useUpdatePatientCoCMMutation();
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);

  const onClickTimeTracker = () => {
    navigate(`/dashboard/patient/${patientId}/time-tracker`);
  };

  useEffect(() => {
    if (coCMStatusId) {
      const status = CoCMStatus.find((item: CoCMStatusType) => item.id === coCMStatusId);
      if (status) {
        setCoCMStatus(status);
      }
    }
  }, [coCMStatusId]);

  useEffect(() => {
    if (episodesOfCare) {
      setEpisodes(episodesOfCare);
    }
  }, [episodesOfCare]);

  useEffect(() => {
    if (careManagerId && providers) {
      const manager = providers.find((user: Provider) => user.id === careManagerId);
      if (manager) {
        setCareManager(manager);
      }
    }
  }, [careManagerId, providers]);

  const showConfirmPopup = async (field: string, newValue: any) => {
    const popupContent = {
      title: t('patient.patientDashboard.coCMSettings.confirmPopup.title', {
        coCMField: field,
      }),
      description: t('patient.patientDashboard.coCMSettings.confirmPopup.description', {
        coCMField: field,
      }),
      btnOk: t('patient.patientDashboard.coCMSettings.confirmPopup.btnOk'),
      btnClose: t('patient.patientDashboard.coCMSettings.confirmPopup.btnClose'),
      toastMessage: t('patient.patientDashboard.coCMSettings.confirmPopup.toastMessage', {
        coCMField: field,
      }),
    };

    setContentPopup(popupContent);
    setOpen(true);
    switch (field) {
      case t('patient.patientDashboard.coCMSettings.coCMStatus'):
        setCoCMStatusIdAction(newValue);
        setCallAction(CoCMCallActionType.CoCMStatus);
        break;
      case t('patient.patientDashboard.coCMSettings.careManager'):
        setCareManagerIdAction(newValue);
        setCallAction(CoCMCallActionType.CareManager);
        break;
      case t('patient.patientDashboard.coCMSettings.episodesOfCare'):
        setEpisodes(newValue);
        setCallAction(CoCMCallActionType.EpisodesOfCare);
        break;
      default:
        break;
    }
  };

  const handleStatusCoCMChange = async (e: any, values: any) => {
    if (values) {
      await showConfirmPopup(
        t('patient.patientDashboard.coCMSettings.coCMStatus'),
        parseInt(values.id)
      );
    }
  };

  const handleCareManagerChange = async (e: any, values: any) => {
    if (values) {
      await showConfirmPopup(
        t('patient.patientDashboard.coCMSettings.careManager'),
        parseInt(values.id)
      );
    }
  };

  const handleEpisodesOfCareChange = async (event: any) => {
    const newValue = event?.target?.value ? parseInt(event?.target?.value) : undefined;
    setEpisodes(newValue);
  };

  const onChangeCoCMSettings = async (successCallback?: () => void) => {
    const coCMStatusId = CoCMStatus.find((item: CoCMStatusType) => item.code === 'D')?.id;
    setOpen(false);
    try {
      switch (callAction) {
        case CoCMCallActionType.CoCMStatus:
          await updateCoCMSetting({
            patientId: patientId!,
            coCMStatusId: coCMStatusIdAction,
          }).unwrap();
          if (coCMStatusIdAction === coCMStatusId) {
            navigate('/dashboard', { replace: true });
          }
          setCoCMStatusIdAction(undefined);
          setCoCMStatus(CoCMStatus.find((item: CoCMStatusType) => item.id === coCMStatusIdAction));
          break;
        case CoCMCallActionType.CareManager:
          await updateCoCMSetting({
            patientId: patientId!,
            careManagerId: careManagerIdAction,
          }).unwrap();
          setCareManagerIdAction(undefined);
          setCareManager(providers.find((user: Provider) => user.id === careManagerIdAction));
          break;
        case CoCMCallActionType.EpisodesOfCare:
          await updateCoCMSetting({
            patientId: patientId!,
            episodesOfCare: episodes,
          }).unwrap();
          if (onReload) {
            onReload();
          }
          break;
        default:
          break;
      }

      if (successCallback) {
        successCallback();
      }
      toast.publish(
        t('patient.patientDashboard.coCMSettings.confirmPopup.toastMessage'),
        'success'
      );
    } catch (e) {
      toast.publish(
        t('patient.patientDashboard.coCMSettings.confirmPopup.toastMessageFail'),
        'error'
      );
    }
  };

  const getCoCMStatusLabel = (option: any) => (option.name ? option.name : '');
  const getCareManagerLabel = (option: any) =>
    option.firstName && option.lastName ? `${option.lastName}, ${option.firstName}` : '';
  const filterCoCMStatusOptions = createFilterOptions({
    matchFrom: 'any',
    stringify: (option: any) => getCoCMStatusLabel(option),
  });
  const filterCareManagerOptions = createFilterOptions({
    matchFrom: 'any',
    stringify: (option: any) => getCareManagerLabel(option),
  });

  const handleOnBlur = () => {
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
    }

    timeoutIdRef.current = setTimeout(async () => {
      if (episodes && episodes > 0 && episodes !== episodesOfCare) {
        await showConfirmPopup(t('patient.patientDashboard.coCMSettings.episodesOfCare'), episodes);
      }
    }, 300);
  };

  return (
    <Box
      sx={{
        marginTop: 1,
        [theme.breakpoints.up('lg')]: {
          marginTop: 0,
          width: '35%',
        },
        [theme.breakpoints.down('lg') && theme.breakpoints.up('md')]: {
          width: '49%',
        },
        [theme.breakpoints.down('md') && theme.breakpoints.up('sm')]: {
          width: '64%',
        },
        [theme.breakpoints.down('sm')]: {
          width: '99%',
        },
      }}
    >
      <StyleLayout>
        <StyleLeftBox>
          <StyleTypo fontWeight={600}>
            {`${t('patient.patientDashboard.coCMSettings.coCMStatus')}:`}
          </StyleTypo>
        </StyleLeftBox>
        <StyleRightBox>
          {isCareManagerOrSuperAdmin(currentUser.roleId) ? (
            <StyleAutocomplete
              data-testid="mui-component-select-cocm-status"
              options={CoCMStatus}
              getOptionLabel={getCoCMStatusLabel}
              onChange={handleStatusCoCMChange}
              value={coCMStatus}
              filterOptions={filterCoCMStatusOptions}
              renderOption={(props: any, option: any) => (
                <li {...props} key={option.id} value={option.id}>
                  <StyleTypo>{option.name}</StyleTypo>
                </li>
              )}
              renderInput={(params: any) => (
                <StyleTextField
                  {...params}
                  margin="normal"
                  size="small"
                  fullWidth
                  label={t('patient.patientDashboard.coCMSettings.coCMStatus')}
                />
              )}
            />
          ) : (
            <StyleTypo>{coCMStatus?.name}</StyleTypo>
          )}
        </StyleRightBox>
      </StyleLayout>

      <StyleLayout>
        <StyleLeftBox>
          <StyleTypo fontWeight={600}>{`${t(
            'patient.patientDashboard.coCMSettings.careManager'
          )}:`}</StyleTypo>
        </StyleLeftBox>
        <StyleRightBox>
          {isCareManagerOrSuperAdmin(currentUser.roleId) ? (
            <StyleAutocomplete
              data-testid="mui-component-select-care-manager"
              options={providers.filter((user: Provider) => user.loginRoleId === Role.CareManager)}
              getOptionLabel={getCareManagerLabel}
              onChange={handleCareManagerChange}
              value={careManager || null}
              filterOptions={filterCareManagerOptions}
              renderOption={(props: any, option: any) => (
                <li {...props} key={option.id} value={option.id}>
                  <StyleTypo>{`${option.lastName}, ${option.firstName}`}</StyleTypo>
                </li>
              )}
              renderInput={(params: any) => (
                <StyleTextField
                  {...params}
                  margin="normal"
                  size="small"
                  fullWidth
                  label={t('patient.patientDashboard.coCMSettings.careManager')}
                />
              )}
            />
          ) : (
            <StyleTypo>
              {careManager?.lastName && careManager?.firstName
                ? `${careManager?.lastName}, ${careManager?.firstName}`
                : ''}
            </StyleTypo>
          )}
        </StyleRightBox>
      </StyleLayout>

      <StyleLayout>
        <StyleLeftBox>
          <StyleTypo fontWeight={600}>{`${t(
            'patient.patientDashboard.coCMSettings.episodesOfCare'
          )}:`}</StyleTypo>
        </StyleLeftBox>
        <StyleRightBox>
          {isCareManagerOrSuperAdmin(currentUser.roleId) ? (
            <StyleTextField
              value={episodes || 0}
              onChange={handleEpisodesOfCareChange}
              onBlur={() => handleOnBlur()}
              margin="normal"
              size="small"
              fullWidth
              label={t('patient.patientDashboard.coCMSettings.episodesOfCare')}
              type="number"
              InputLabelProps={{
                shrink: true,
              }}
              variant="outlined"
              error={episodes === undefined || episodes < 1}
              helperText={
                episodes === undefined || episodes < 1
                  ? t('patient.patientDashboard.coCMSettings.error.episodesOfCareInvalid')
                  : ''
              }
            />
          ) : (
            <StyleTypo>{episodesOfCare}</StyleTypo>
          )}
        </StyleRightBox>
      </StyleLayout>
      <StyleLayout sx={{ mt: canViewTimeTracker(currentUser.roleId) ? 1 : 0}}>
        <StyleLeftBox>
          <StyleTypo fontWeight={600}>{getCurrentMonthTimeTracker()} Time Tracker:</StyleTypo>
        </StyleLeftBox>
        <StyleRightBox sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
           <StyleTypo>{getNumberInUSFormat(totalTimeTrackerMinutes ?? 0)} minutes</StyleTypo>
            { canViewTimeTracker(currentUser.roleId) && (<Box
              sx={{
                backgroundColor: theme.palette.primary.main,
                borderRadius: '20%',
              }}
            >
              <IconButton
                size="medium"
                onClick={onClickTimeTracker}
              >
                <MoreTimeIcon sx={{ color: 'white', fontSize: 24 }} />
              </IconButton>
            </Box>)}
        </StyleRightBox>
      </StyleLayout>

      <PopupModal
        isOpen={isOpen}
        contentPopup={contentPopup}
        onClick={onChangeCoCMSettings}
        onClose={() => {
          if (callAction === CoCMCallActionType.EpisodesOfCare) {
            setEpisodes(episodesOfCare);
          }
          setOpen(false);
        }}
      />
    </Box>
  );
}
